import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { enrichCountryCodes } from '../../utils/countries';

function PersonalInfoStep({ formData, updateFormData, onValidationChange }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentLanguage = i18n.language?.split('-')[0] || 'en';
  const currentYear = new Date().getFullYear();

  const [supportedCountries, setSupportedCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(true);

  // Generate year options
  const birthYearOptions = useMemo(() => {
    const years = [];
    for (let year = currentYear; year >= currentYear - 100; year--) {
      years.push(year);
    }
    return years;
  }, [currentYear]);

  const diagnosisYearOptions = useMemo(() => {
    const years = [];
    for (let year = currentYear; year >= currentYear - 100; year--) {
      years.push(year);
    }
    return years;
  }, [currentYear]);

  // Fetch supported countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await api.get('/config/supported-countries');
        const enriched = enrichCountryCodes(response.data, currentLanguage);
        setSupportedCountries(enriched);
      } catch (err) {
        console.error('Failed to fetch supported countries:', err);
      } finally {
        setCountriesLoading(false);
      }
    };
    fetchCountries();
  }, [currentLanguage]);

  const validateForm = () => {
    // Check all required fields
    const isValid =
      formData.birthYear &&
      formData.diabetesDiagnosisYear &&
      formData.diabetesType &&
      formData.country &&
      // Diagnosis year must be after birth year
      formData.diabetesDiagnosisYear >= formData.birthYear;

    // Report validation state to parent (for button control)
    if (onValidationChange) {
      onValidationChange(isValid);
    }

    return isValid;
  };

  // Validate when form data changes
  useEffect(() => {
    validateForm();
  }, [formData.birthYear, formData.diabetesDiagnosisYear, formData.diabetesType, formData.country]);

  const selectedCountry = supportedCountries.find(c => c.code === formData.country) || null;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('onboarding.personal.heading')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('onboarding.personal.description')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Birth Year */}
        <FormControl fullWidth>
          <InputLabel id="birth-year-label">{t('onboarding.personal.birthYear')}</InputLabel>
          <Select
            labelId="birth-year-label"
            value={formData.birthYear}
            label={t('onboarding.personal.birthYear')}
            onChange={(e) => {
              updateFormData({ birthYear: e.target.value });
              // Reset diagnosis year if it's before the new birth year
              if (formData.diabetesDiagnosisYear && formData.diabetesDiagnosisYear < e.target.value) {
                updateFormData({ diabetesDiagnosisYear: '' });
              }
            }}
            MenuProps={{
              PaperProps: {
                sx: { maxHeight: 300 },
              },
            }}
          >
            {birthYearOptions.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Diagnosis Year */}
        <FormControl fullWidth>
          <InputLabel id="diagnosis-year-label">{t('onboarding.personal.diagnosisYear')}</InputLabel>
          <Select
            labelId="diagnosis-year-label"
            value={formData.diabetesDiagnosisYear}
            label={t('onboarding.personal.diagnosisYear')}
            onChange={(e) => updateFormData({ diabetesDiagnosisYear: e.target.value })}
            MenuProps={{
              PaperProps: {
                sx: { maxHeight: 300 },
              },
            }}
          >
            {diagnosisYearOptions.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Diabetes Type */}
        <FormControl component="fieldset">
          <FormLabel component="legend">{t('onboarding.personal.diabetesType')}</FormLabel>
          <RadioGroup
            row={!isMobile}
            value={formData.diabetesType}
            onChange={(e) => updateFormData({ diabetesType: e.target.value })}
          >
            <FormControlLabel
              value="type1"
              control={<Radio />}
              label={t('onboarding.personal.type1')}
            />
            <FormControlLabel
              value="type2"
              control={<Radio />}
              label={t('onboarding.personal.type2')}
            />
          </RadioGroup>
        </FormControl>

        {/* Country */}
        <FormControl fullWidth>
          {countriesLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                {t('common.loading')}
              </Typography>
            </Box>
          ) : (
            <Autocomplete
              options={supportedCountries}
              getOptionLabel={(option) => option.name}
              value={selectedCountry}
              onChange={(event, newValue) => {
                updateFormData({ country: newValue?.code || '' });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('onboarding.personal.country')}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <span>{option.name}</span>
                      <Chip label={option.code} size="small" variant="outlined" />
                    </Box>
                  </li>
                );
              }}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              noOptionsText={t('onboarding.personal.noCountriesAvailable')}
            />
          )}
        </FormControl>

        {supportedCountries.length === 0 && !countriesLoading && (
          <Alert severity="warning">
            {t('onboarding.personal.noCountriesWarning')}
          </Alert>
        )}
      </Box>
    </Box>
  );
}

export default PersonalInfoStep;
